import React, { useState } from "react";
import {
  Box,
  Card,
  CardMedia,
  CardContent,
  Typography,
  Grid,
  IconButton,
} from "@mui/material";
import {
  TrendingUp,
  Visibility,
  Edit,
  Delete,
  CalendarToday,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { LocationModel } from "../../object-models/location-model";
import Organization_1 from "../../assets/Images/client/Organization_1.png";
import SnackBar from "components/SnackBar/SnackBar";
import AlertDialog from "components/AlertDialog";
import { ConfirmationMessages } from "constants/constant";
import { useLocationDelete } from "hooks/useLocationDelete";
import FormDialog from "components/Dialog/modal-popup";
import CreateUpdateLocation from "pages/CreateUpdateLocation";

interface LocationCardsProps {
  locations: LocationModel[];
  onLocationDeleted?: (locationId: number) => void;
  onLocationUpdated?: () => void;
}

const LocationCards: React.FC<LocationCardsProps> = ({
  locations,
  onLocationDeleted, onLocationUpdated 
}) => {
  const navigate = useNavigate();
  const {
    openModal,
    success,
    setSuccess,
    selectedLocationName,
    handleDelete,
    openDialog,
    handleClose,
    handleConfirm,
  } = useLocationDelete(onLocationDeleted);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTitle, setDialogTitle] = useState("");

  const handleView = (locationId: number) => {
  };

  const hideEditDialogHandler = () => {
    hideDialogHandler();
    onLocationUpdated?.();
  };
  
  const hideDialogHandler = () => {
    setDialogOpen(false);
  };
  
    const handleEdit = (locationId: number) => {
    localStorage.setItem("LocationId", String(locationId));
    setDialogTitle("Edit Location Details");
    setDialogOpen(true);
  };

  const getTotalEmissions = (location: LocationModel) => {
    const scope1 = location.scope1GHGEmission ?? 0;
    const scope2 = location.scope2GHGEmission ?? 0;
    const scope3 = location.scope3GHGEmission ?? 0;
    return scope1 + scope2 + scope3;
  };

  const handleLocationClick = (locationId: number) => {
    localStorage.removeItem("subAreaName");
    localStorage.setItem("LocationId", String(locationId));
    localStorage.setItem("Portfolioview", "0");
    navigate(`/Location/${locationId}/Location-Details`);
  };


  return (
    <>
      <Grid container spacing={2}>
        {locations.map((location) => (
          <Grid item xs={12} sm={6} md={2.4} key={location.locationId}>
            <Card
              onClick={() => handleLocationClick(location.locationId)}
              sx={{
                borderRadius: 2,
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                transition: "transform 0.2s, box-shadow 0.2s",
                cursor: "pointer",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                },
              }}
            >
              <CardMedia
                component="img"
                height="70"
                image={Organization_1}
                alt={location.locationName}
                sx={{ objectFit: "cover" }}
              />
              <CardContent sx={{ p: 1.25, "&:last-child": { pb: 1.25 } }}>
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  sx={{ mb: 0.75, fontSize: "0.875rem", lineHeight: 1.3 }}
                >
                  {location.locationName ?? "Location Name"}
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    mb: 0.5,
                  }}
                >
                  <TrendingUp sx={{ fontSize: 14, color: "text.secondary" }} />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: "0.75rem" }}
                  >
                    {getTotalEmissions(location).toLocaleString()} tCO₂e
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <CalendarToday
                      sx={{ fontSize: 12, color: "text.secondary" }}
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: "0.7rem" }}
                    >
                      {location.baseYear ?? "N/A"}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", gap: 0.75 }}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(location.locationId);
                      }}
                      sx={{
                        padding: "2px",
                        color: "primary.main",
                        "&:hover": {
                          bgcolor: "primary.light",
                          color: "primary.dark",
                        },
                      }}
                    >
                      <Edit sx={{ fontSize: 14 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(
                          location.locationId,
                          e,
                          location.locationName,
                        );
                      }}
                      sx={{
                        padding: "2px",
                        color: "primary.main",
                        "&:hover": {
                          bgcolor: "error.light",
                          color: "error.main",
                        },
                      }}
                    >
                      <Delete sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
              <FormDialog
        dialogOpen={dialogOpen}
        hideDialogHandler={hideDialogHandler}
        dialogTitle={dialogTitle}
        isFullScreenAllowed={true}
        maxWidth="lg"
        childComponent={
          <CreateUpdateLocation isEdit={true} closeDialog={hideEditDialogHandler} />
        }
      />
      <AlertDialog
        open={openModal}
        openDialog={openDialog}
        handleClose={handleClose}
        handleConfirm={handleConfirm}
        locationName={selectedLocationName}
        confirmationMessage={ConfirmationMessages.locationConfirmation}
      />
      <SnackBar
        show={success.show}
        message={success.message}
        severity={success.severity}
        onclose={() =>
          setSuccess({ show: false, severity: "info", message: "" })
        }
      />
    </>
  );
};

export default LocationCards;
